import React, { useState, useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Palette,
  TrendingUp,
  Code2,
  Cpu,
  PenTool,
  Pen,
  CheckCircle2,
  Sparkles,
  Rocket,
  Layers,
  Trophy,
  Store,
  Star,
  Zap,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { useScrollToHash } from "../../hooks/useScrollToHash";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };
const slideIn = (dir) => ({
  hidden: { opacity: 0, x: dir, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
});

/* ─── SERVICES DATA ─── */
const services = [
  {
    id: "branding",
    icon: Palette,
    title: "Branding & Identity",
    tagline: "Make your business unforgettable",
    desc: "Most brands get forgotten in a week. We build ones that don't. We don't just make logos—we create complete systems your team can actually use (and that customers actually remember).",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=800&q=80",
    gradient: "from-violet-500 to-purple-600",
    light: "bg-violet-50",
    iconColor: "text-violet-600",
    features: [
      "Your logo works everywhere: billboards, apps, emails, business cards",
      "You know exactly how you're different from competitors",
      "Everyone on your team uses the same colors, fonts, and voice",
      "Every touchpoint looks professionally consistent",
      "Your social media looks cohesive across every platform",
      "Your team knows what to say and how to say it",
      "Your packaging tells your brand story before customers open the box",
      "Your brand stays fresh without losing what makes it recognizable",
    ],
    stats: { projects: "45+", satisfaction: "99%", time: "2-4 weeks" },
  },
  {
    id: "marketing",
    icon: TrendingUp,
    title: "Digital Marketing",
    tagline: "Drive real, measurable growth",
    desc: "We don't just run campaigns—we understand your market. SEO, social, ads, content: we connect all of it so your message hits the right people at the right time. In emerging markets, we know what works because we've built it here.",
    image:
      "https://images.unsplash.com/photo-1533750516457-a7f992034fec?auto=format&fit=crop&w=800&q=80",
    gradient: "from-purple-500 to-indigo-600",
    light: "bg-purple-50",
    iconColor: "text-purple-600",
    features: [
      "People find you when they search for what you sell",
      "Your audience actually engages with what you post",
      "You only pay when people interested in you click",
      "Reach customers exactly where they spend time online",
      "You're solving customer problems, not just talking at them",
      "Customers hear from you at the right time with the right message",
      "Real people your customers trust tell their followers about you",
      "You see exactly what's working and what's not",
    ],
    stats: { projects: "60+", satisfaction: "97%", time: "Ongoing" },
  },
  {
    id: "development",
    icon: Code2,
    title: "Web & App Development",
    tagline: "Build your digital foundation",
    desc: "We build fast, secure apps that work on every device. But here's what's different: we know the constraints of emerging markets. Slow internet? We optimize for that. Need offline functionality? We've built it. Tight budget? We find solutions that work.",
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80",
    gradient: "from-indigo-500 to-blue-600",
    light: "bg-indigo-50",
    iconColor: "text-indigo-600",
    features: [
      "Your website actually works the way your business operates",
      "Your app stays fast even when thousands of users are online",
      "Customers can use your app on any phone",
      "Customers can buy from you 24/7 without calling",
      "Your app works online and offline",
      "Your tools talk to each other automatically",
      "Your data is organized, secure, and always accessible",
      "We keep your app running smoothly so you focus on business",
    ],
    stats: { projects: "35+", satisfaction: "98%", time: "4-8 weeks" },
  },
  {
    id: "design",
    icon: PenTool,
    title: "UI/UX Design",
    tagline: "Interfaces users love to use",
    desc: "Your app won't work if nobody can figure it out. We research your actual users (not imaginary ones), test with them constantly, and build interfaces that make sense. The result: fewer support calls, happier users, better conversions.",
    image:
      "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?auto=format&fit=crop&w=800&q=80",
    gradient: "from-fuchsia-500 to-pink-600",
    light: "bg-fuchsia-50",
    iconColor: "text-fuchsia-600",
    features: [
      "We talk to your actual users before designing anything",
      "People find what they need without getting confused",
      "You see exactly how it works before we build it",
      "The final design looks and feels exactly right",
      "Your entire product works consistently everywhere",
      "We test with real users and fix what doesn't work",
      "Your design looks beautiful on every screen size",
      "Every click feels thoughtful and responds instantly",
    ],
    stats: { projects: "40+", satisfaction: "99%", time: "3-6 weeks" },
  },
  {
    id: "ai",
    icon: Cpu,
    title: "AI Automation",
    tagline: "AI that gets your business",
    desc: "Most AI tools feel robotic because they are. Ours understand context. Your chatbot doesn't just answer questions—it reads the room. Your workflow automation adapts to how your team actually works. Result: less busywork, fewer handoffs, happier customers.",
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80",
    gradient: "from-violet-500 to-fuchsia-600",
    light: "bg-violet-50",
    iconColor: "text-violet-500",
    features: [
      "Your chatbot answers questions 24/7 in your customer's language",
      "Customers text you and get immediate answers",
      "Tasks that took hours now happen automatically",
      "See patterns in your business data automatically",
      "Your AI actually understands your specific business",
      "Understand your global customers in their language",
      "Bad leads are filtered out automatically",
      "Your AI works with the tools you already use",
    ],
    stats: { projects: "25+", satisfaction: "98%", time: "2-6 weeks" },
  },
  {
    id: "content",
    icon: Pen,
    title: "Content Creation",
    tagline: "Content that converts",
    desc: "Nobody cares about your story—they care about solving their problem. We write and film with that in mind. Every piece serves a purpose: build trust, explain why you're different, or move someone closer to buying. No fluff. Just what works.",
    image:
      "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=800&q=80",
    gradient: "from-purple-500 to-violet-600",
    light: "bg-purple-50",
    iconColor: "text-purple-500",
    features: [
      "Your website tells visitors exactly why they should choose you",
      "You attract customers who are looking for answers you provide",
      "You have a month of content ready to go",
      "Your story comes alive in video format",
      "Complex ideas become simple and entertaining",
      "Data becomes visual and memorable",
      "Your products look incredible in every shot",
      "Your ads actually get people to click",
    ],
    stats: { projects: "55+", satisfaction: "96%", time: "Ongoing" },
  },
];

/* ─── HERO ─── */
const ServicesHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-violet-300/20 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300/15 rounded-full blur-[120px] animate-float-orb-reverse" />
      {/* Decorative morphing blob */}
      <div className="absolute top-20 right-[10%] w-72 h-72 bg-gradient-to-br from-violet-200/30 to-purple-300/20 animate-morph-blob blur-2xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm text-slate-600"
          >
            <Layers className="w-4 h-4 text-violet-600" /> Our Services
          </motion.span>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]"
          >
            Everything your business{" "}
            <span className="text-gradient-vibrant">needs to grow</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-6 text-lg text-slate-500 max-w-xl leading-relaxed"
          >
            From the first logo sketch to AI-powered automation. We cover every
            stage of your digital journey, all under one roof.
          </motion.p>
        </motion.div>
        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotate: 2 }}
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block"
        >
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-apple-xl animate-glow-pulse">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80"
                alt="Team collaboration"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            {/* Floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="absolute -bottom-6 -left-6 glass-strong rounded-2xl p-4 shadow-apple-lg animate-float-slow"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Client Rating</p>
                  <p className="text-lg font-bold text-slate-900">4.9/5.0</p>
                </div>
              </div>
            </motion.div>
            {/* Floating service count */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
              className="absolute -top-4 -right-4 glass-strong rounded-2xl p-3 shadow-apple animate-float-slow"
              style={{ animationDelay: "-3s" }}
            >
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-violet-600" />
                <span className="text-sm font-bold text-slate-700">
                  6+ Services
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Quick nav pills — scrollable on mobile */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-8 sm:mt-10 scroll-pills sm:flex sm:flex-wrap sm:gap-2 sm:items-center"
      >
        {services.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full text-sm font-medium text-slate-600 hover:text-violet-700 hover:bg-violet-50/60 transition-all whitespace-nowrap flex-shrink-0"
          >
            <s.icon className={`w-4 h-4 ${s.iconColor}`} />
            <span className="hidden sm:inline">{s.title}</span>
            <span className="sm:hidden">{s.title.split(" ")[0]}</span>
          </a>
        ))}
        {/* Offline Marketing — standalone highlighted link */}
        <Link
          to="/offline-marketing"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-apple hover:shadow-apple-lg hover:scale-[1.03] transition-all duration-300"
        >
          <Store className="w-4 h-4" />
          <span>Offline Marketing</span>
          <span className="hidden sm:inline text-xs bg-white/20 rounded-full px-2 py-0.5">
            New
          </span>
        </Link>
      </motion.div>
    </div>
  </section>
);

/* ─── SERVICE DETAIL SECTION ─── */
const ServiceSection = ({ service, index }) => {
  const isEven = index % 2 === 0;

  return (
    <section
      id={service.id}
      className={`py-14 sm:py-20 md:py-28 scroll-mt-20 ${index % 2 !== 0 ? "bg-violet-tint" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center ${!isEven ? "lg:grid-flow-dense" : ""}`}
        >
          {/* Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className={!isEven ? "lg:col-start-2" : ""}
          >
            <motion.div
              variants={fadeUp}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${service.light} text-sm font-semibold ${service.iconColor}`}
            >
              <service.icon className="w-4 h-4" /> {service.tagline}
            </motion.div>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="mt-3 sm:mt-4 text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-tight"
            >
              {service.title}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-500 leading-relaxed"
            >
              {service.desc}
            </motion.p>

            {/* Features grid */}
            <motion.div
              variants={fadeUp}
              custom={3}
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {service.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <span className="text-sm text-slate-600">{f}</span>
                </div>
              ))}
            </motion.div>

            {/* Stats row */}
            <motion.div
              variants={fadeUp}
              custom={4}
              className="mt-8 flex flex-wrap gap-6"
            >
              <div>
                <p className="text-2xl font-bold text-gradient">
                  {service.stats.projects}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Projects</p>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <p className="text-2xl font-bold text-gradient">
                  {service.stats.satisfaction}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Satisfaction</p>
              </div>
              <div className="w-px bg-slate-200" />
              <div>
                <p className="text-2xl font-bold text-gradient">
                  {service.stats.time}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Delivery</p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={5} className="mt-8">
              <Link
                to="/contact"
                className="btn-shine inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg hover:scale-[1.02] transition-all"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideIn(isEven ? 60 : -60)}
            className={!isEven ? "lg:col-start-1" : ""}
          >
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-apple-xl group card-shine animate-glow-pulse">
              <img
                src={service.image}
                alt={service.title}
                className="w-full aspect-[4/3] object-cover img-parallax"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 glass-strong rounded-xl px-4 py-2 shadow-apple">
                <div className="flex items-center gap-2">
                  <service.icon className={`w-5 h-5 ${service.iconColor}`} />
                  <span className="text-sm font-semibold text-slate-700">
                    {service.title}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── OFFLINE MARKETING CTA BANNER ─── */
const OfflineMarketingBanner = () => (
  <section className="py-14 sm:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 shadow-apple-xl"
      >
        <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl animate-float-orb" />
        <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl animate-float-orb-reverse" />
        <div className="relative z-10 p-8 sm:p-12 md:p-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-sm font-semibold text-white mb-4">
                <Store className="w-4 h-4" /> Offline Marketing
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight">
                Signage & In-Store Branding
              </h2>
              <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed max-w-lg">
                Beyond the digital: professional signage, 3D letters, LED
                displays, wall graphics, and custom in-store decor that makes
                your brand impossible to miss.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/offline-marketing"
                  className="btn-shine group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-amber-700 font-semibold rounded-xl hover:bg-amber-50 hover:scale-[1.02] transition-all duration-300 shadow-apple-lg"
                >
                  Explore Offline Services{" "}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
            <div className="hidden lg:grid grid-cols-2 gap-3">
              <div className="space-y-3">
                {[
                  "3D Letter Signage",
                  "LED Signage",
                  "ACP Boards",
                  "Glow Signs",
                ].map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur"
                  >
                    <CheckCircle2 className="w-4 h-4 text-white/80 flex-shrink-0" />
                    <span className="text-sm text-white/90 font-medium">
                      {s}
                    </span>
                  </div>
                ))}
              </div>
              <div className="space-y-3 pt-4">
                {[
                  "Wall Graphics",
                  "Retail Branding",
                  "Smart Frames",
                  "Window Films",
                ].map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur"
                  >
                    <CheckCircle2 className="w-4 h-4 text-white/80 flex-shrink-0" />
                    <span className="text-sm text-white/90 font-medium">
                      {s}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── CTA ─── */
const CTASection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 sm:p-12 md:p-20 text-center shadow-apple-xl">
        <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl animate-float-orb" />
        <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl animate-float-orb-reverse" />
        <div className="absolute inset-0 animate-rotate-slow opacity-20">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 border border-white/20 rounded-full" />
          <div className="absolute bottom-1/3 right-1/3 w-20 h-20 border border-white/10 rounded-full" />
        </div>
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex p-4 rounded-full bg-white/10 backdrop-blur-xl mb-6">
              <Sparkles className="w-8 h-8 text-white/80" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-5">
              Not sure where to start?
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8 sm:mb-10">
              Tell us about your business. We'll recommend the perfect
              combination of services and build a custom proposal, free of
              charge.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="btn-shine group inline-flex items-center gap-3 px-8 py-4 bg-white text-violet-700 font-semibold rounded-2xl hover:bg-violet-50 hover:scale-[1.02] transition-all duration-300 shadow-apple-lg"
              >
                Get Free Consultation{" "}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-4 border border-white/30 text-white font-semibold rounded-2xl hover:bg-white/10 transition-all duration-300"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const ServicesPage = () => {
  useScrollToHash();

  return (
    <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
      <Header />
      <ServicesHero />
      {services.map((s, i) => (
        <ServiceSection key={s.id} service={s} index={i} />
      ))}
      <OfflineMarketingBanner />
      <CTASection />
      <Footer />
    </div>
  );
};

export default ServicesPage;
